import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
} from 'react';
import {
  BANNER_SNAP_VIDEO_TOP_PCT,
  BANNER_SNAP_VIDEO_BOTTOM_PCT,
} from '@content-engine/shared';

export interface BannerPlacementValue {
  positionXPct: number;
  positionYPct: number;
  sizePct: number;
  crop: { top: number; right: number; bottom: number; left: number };
}

export interface ChromaPreviewParams {
  color: string;       // '#RRGGBB' or '0xRRGGBB' — both accepted
  similarity: number;  // 0..1 — ffmpeg's chromakey similarity
  blend: number;       // 0..1 — ffmpeg's chromakey blend (soft edge)
}

export interface BannerPlacementEditorProps {
  /** 9:16 foundation media (presigned URL). */
  clipPresignedUrl: string;
  banner: { mediaUrl: string; kind: 'image' | 'video' };
  value: BannerPlacementValue;
  onChange: (next: BannerPlacementValue) => void;
  /** When set AND not in cropMode, the banner media is rendered through a
   *  canvas chroma-key approximation so the moderator can visually verify
   *  the keying params. ffmpeg renders the final cut. */
  chromaPreview?: ChromaPreviewParams | null;
  /** Eyedropper mode: when true, drag/resize/crop are disabled, the cursor
   *  is a crosshair over the banner, and a click samples the pixel colour
   *  from the ORIGINAL banner media (not the keyed preview) via onPickColor. */
  pickingColor?: boolean;
  /** Receives the sampled colour as 'RRGGBB' (no '#'/'0x' prefix). */
  onPickColor?: (hexRRGGBB: string) => void;
  /** Called when sampling isn't possible. `reason` distinguishes:
   *  - 'not-ready': the sampling source hasn't loaded its natural dimensions
   *    yet (click landed right after the modal opened) — a retry is likely
   *    to succeed once it finishes loading.
   *  - 'tainted' (or omitted): the canvas is tainted (R2 CORS missing on the
   *    banner media), or the sampled window was fully transparent — retrying
   *    won't help. */
  onPickUnavailable?: (reason?: 'tainted' | 'not-ready') => void;
}

type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
type CropSide = 'top' | 'right' | 'bottom' | 'left';

type DragMode =
  | { kind: 'move'; startX: number; startY: number; origX: number; origY: number }
  | { kind: 'resize'; handle: ResizeHandle; startX: number; startY: number; origSize: number; origX: number; origY: number }
  | { kind: 'crop'; side: CropSide; startCoord: number; origCrop: number };

const SNAP_THRESHOLD_PCT = 1; // ±1% of clip dimension

export function BannerPlacementEditor(props: BannerPlacementEditorProps): ReactElement {
  const canvasRef = useRef<HTMLDivElement>(null);
  const [mediaAspect, setMediaAspect] = useState<number | null>(null); // banner natural W/H
  const [cropMode, setCropMode] = useState(false);
  const [drag, setDrag] = useState<DragMode | null>(null);

  // ---- Eyedropper sampling source ----
  // Dedicated, hidden <img>/<video> holding the ORIGINAL banner media at
  // natural size, independent of whatever's currently on screen (raw banner
  // OR the keyed canvas preview). This guarantees we always sample the true
  // source pixel, never a chroma-keyed approximation.
  const pickImgRef = useRef<HTMLImageElement>(null);
  const pickVideoRef = useRef<HTMLVideoElement>(null);
  const pickCanvasRef = useRef<HTMLCanvasElement | null>(null);

  // Entering picking mode while cropMode is active would make the click→pixel
  // math wrong (cropMode shows the FULL uncropped banner, not the cropped
  // view the coordinate formula assumes) — force crop mode off.
  useEffect(() => {
    if (props.pickingColor && cropMode) setCropMode(false);
  }, [props.pickingColor]);

  // Banner aspect AFTER crop (what's visible on the clip).
  const croppedAspect = useMemo(() => {
    if (!mediaAspect) return null;
    const cw = 1 - (props.value.crop.left + props.value.crop.right) / 100;
    const ch = 1 - (props.value.crop.top + props.value.crop.bottom) / 100;
    if (cw <= 0 || ch <= 0) return null;
    return (mediaAspect * cw) / ch;
  }, [mediaAspect, props.value.crop]);

  // Banner width as % of clip width = sizePct directly.
  // Banner height as % of clip height = sizePct * (clipW/clipH) / aspect
  //                                   = sizePct * (9/16) / aspect.
  // In crop mode we lay out the overlay at the banner's NATURAL aspect
  // (mediaAspect) so the full banner is visible at correct proportions
  // and the crop mask shows what would be trimmed. In place mode we use
  // the post-crop aspect so the WYSIWYG matches the final ffmpeg render.
  const layoutAspect = cropMode ? mediaAspect : croppedAspect;
  const bannerWidthPct = props.value.sizePct;
  const bannerHeightPct = layoutAspect ? (props.value.sizePct * (9 / 16)) / layoutAspect : props.value.sizePct;

  // ---- Pointer math ----
  function canvasRect(): DOMRect {
    return canvasRef.current!.getBoundingClientRect();
  }
  function pctFromPointer(e: PointerEvent): { xPct: number; yPct: number } {
    const r = canvasRect();
    return {
      xPct: ((e.clientX - r.left) / r.width) * 100,
      yPct: ((e.clientY - r.top) / r.height) * 100,
    };
  }

  // Snap candidates are values for the banner's TOP-LEFT corner. Three
  // categories of magnet line per axis:
  //   1. clip frame edges (0, 100-banner)
  //   2. blur-band inner edges (the un-blurred video edges)
  //   3. center-line snaps — three flavours: banner's TOP edge on the line,
  //      banner's CENTER on the line, banner's BOTTOM edge on the line.
  //      All three are useful: "sit just above center", "centred", "sit just
  //      below center".
  function snapY(yPct: number, bannerHPct: number): number {
    const candidates = [
      0,                                              // top of clip
      BANNER_SNAP_VIDEO_TOP_PCT,                      // banner top on video-top guide
      BANNER_SNAP_VIDEO_TOP_PCT - bannerHPct,         // banner bottom on video-top guide (banner sits in top blur band)
      BANNER_SNAP_VIDEO_BOTTOM_PCT,                   // banner top on video-bottom guide (banner sits in bottom blur band)
      BANNER_SNAP_VIDEO_BOTTOM_PCT - bannerHPct,      // banner bottom on video-bottom guide
      100 - bannerHPct,                               // bottom of clip
      50,                                             // banner top on horizontal-center guide
      50 - bannerHPct / 2,                            // banner center on horizontal-center guide
      50 - bannerHPct,                                // banner bottom on horizontal-center guide
    ];
    for (const c of candidates) {
      if (Math.abs(yPct - c) <= SNAP_THRESHOLD_PCT) return c;
    }
    return yPct;
  }
  function snapX(xPct: number, bannerWPct: number): number {
    const candidates = [
      0,                                              // left edge of clip
      100 - bannerWPct,                               // right edge of clip
      50,                                             // banner left on vertical-center guide
      50 - bannerWPct / 2,                            // banner center on vertical-center guide
      50 - bannerWPct,                                // banner right on vertical-center guide
    ];
    for (const c of candidates) {
      if (Math.abs(xPct - c) <= SNAP_THRESHOLD_PCT) return c;
    }
    return xPct;
  }

  // ---- Drag handlers ----
  function onBannerPointerDown(e: PointerEvent): void {
    if (cropMode || props.pickingColor) return; // no drag while picking a colour
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const { xPct, yPct } = pctFromPointer(e);
    setDrag({
      kind: 'move',
      startX: xPct,
      startY: yPct,
      origX: props.value.positionXPct,
      origY: props.value.positionYPct,
    });
  }
  function onHandlePointerDown(handle: ResizeHandle, e: PointerEvent): void {
    if (props.pickingColor) return; // no resize/crop while picking a colour
    e.stopPropagation();
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    const { xPct, yPct } = pctFromPointer(e);
    if (cropMode) {
      const sideMap: Record<ResizeHandle, CropSide | null> = {
        n: 'top', s: 'bottom', e: 'right', w: 'left', nw: null, ne: null, se: null, sw: null,
      };
      const side = sideMap[handle];
      if (!side) return; // corners not used in crop mode
      setDrag({
        kind: 'crop',
        side,
        startCoord: side === 'top' || side === 'bottom' ? yPct : xPct,
        origCrop: props.value.crop[side],
      });
    } else {
      setDrag({
        kind: 'resize',
        handle,
        startX: xPct,
        startY: yPct,
        origSize: props.value.sizePct,
        origX: props.value.positionXPct,
        origY: props.value.positionYPct,
      });
    }
  }
  function onPointerMove(e: PointerEvent): void {
    if (!drag) return;
    const { xPct, yPct } = pctFromPointer(e);

    if (drag.kind === 'move') {
      const newX = clamp(drag.origX + (xPct - drag.startX), 0, 100 - bannerWidthPct);
      const newY = clamp(drag.origY + (yPct - drag.startY), 0, 100 - bannerHeightPct);
      props.onChange({
        ...props.value,
        positionXPct: snapX(newX, bannerWidthPct),
        positionYPct: snapY(newY, bannerHeightPct),
      });
      return;
    }
    if (drag.kind === 'resize') {
      // For corner/edge resize, derive new size from pointer offset from
      // the anchor corner. Aspect preserved (v1).
      const dx = xPct - drag.startX;
      const dy = yPct - drag.startY;
      // se: enlarges DOWN+RIGHT  → size grows with +dx,+dy
      // nw: enlarges UP+LEFT     → size grows with -dx,-dy
      const sign = signForHandle(drag.handle);
      // Average ONLY active axes — cardinal handles (n/s/e/w) shouldn't divide by 2
      // (otherwise cursor must travel 2× for the banner to follow).
      const activeAxes = (sign.x !== 0 ? 1 : 0) + (sign.y !== 0 ? 1 : 0);
      const delta = (sign.x * dx + sign.y * dy) / Math.max(activeAxes, 1);
      // Round to integer — sizePct is z.number().int() on the API, and the
      // editor used to emit fractional values like 23.7 which the create-
      // variant route rejected with a generic "Validation failed" response.
      const newSize = Math.round(clamp(drag.origSize + delta, 10, 100));
      // Anchor opposite corner so the OPPOSITE side stays put.
      const anchor = anchorForHandle(drag.handle, drag.origX, drag.origY, drag.origSize, bannerHeightPct);
      const newWidth = newSize;
      const newHeight = croppedAspect ? (newSize * (9 / 16)) / croppedAspect : newSize;
      const newX = clamp(anchor.anchorRight  ? anchor.x - newWidth  : anchor.x, 0, 100 - newWidth);
      const newY = clamp(anchor.anchorBottom ? anchor.y - newHeight : anchor.y, 0, 100 - newHeight);
      props.onChange({ ...props.value, sizePct: newSize, positionXPct: newX, positionYPct: newY });
      return;
    }
    if (drag.kind === 'crop') {
      // Convert pointer delta into a banner-local crop value.
      const bannerR = bannerRectPct();
      let newCrop = drag.origCrop;
      if (drag.side === 'top') {
        const local = ((yPct - bannerR.top) / bannerR.height) * 100;
        newCrop = clamp(local, 0, 95);
      } else if (drag.side === 'bottom') {
        const local = ((bannerR.top + bannerR.height - yPct) / bannerR.height) * 100;
        newCrop = clamp(local, 0, 95);
      } else if (drag.side === 'left') {
        const local = ((xPct - bannerR.left) / bannerR.width) * 100;
        newCrop = clamp(local, 0, 95);
      } else if (drag.side === 'right') {
        const local = ((bannerR.left + bannerR.width - xPct) / bannerR.width) * 100;
        newCrop = clamp(local, 0, 95);
      }
      // Prevent opposite-side overlap (combined ≤ 95).
      const opposite =
        drag.side === 'top'
          ? props.value.crop.bottom
          : drag.side === 'bottom'
            ? props.value.crop.top
            : drag.side === 'left'
              ? props.value.crop.right
              : props.value.crop.left;
      newCrop = Math.min(newCrop, 95 - opposite);
      props.onChange({ ...props.value, crop: { ...props.value.crop, [drag.side]: newCrop } });
    }
  }
  function onPointerUp(): void { setDrag(null); }

  function snapTo(preset: 'top-band' | 'center' | 'bottom-band'): void {
    const hCenterX = (100 - bannerWidthPct) / 2;
    let newY: number;
    if (preset === 'top-band') {
      // banner sits IN the top blur band: its bottom touches BANNER_SNAP_VIDEO_TOP_PCT
      newY = Math.max(0, BANNER_SNAP_VIDEO_TOP_PCT - bannerHeightPct);
    } else if (preset === 'bottom-band') {
      // banner sits IN the bottom blur band: its top touches BANNER_SNAP_VIDEO_BOTTOM_PCT
      newY = Math.min(100 - bannerHeightPct, BANNER_SNAP_VIDEO_BOTTOM_PCT);
    } else {
      newY = 50 - bannerHeightPct / 2;
    }
    props.onChange({ ...props.value, positionXPct: hCenterX, positionYPct: newY });
  }

  function bannerRectPct(): { left: number; top: number; width: number; height: number } {
    return {
      left: props.value.positionXPct,
      top: props.value.positionYPct,
      width: bannerWidthPct,
      height: bannerHeightPct,
    };
  }

  // ---- Eyedropper click ----
  // Fires on a plain click over the banner overlay (onBannerPointerDown
  // bails out early when pickingColor, so it never calls stopPropagation/
  // setPointerCapture/preventDefault — the browser's native click still
  // reaches us on pointerup).
  function onBannerClick(e: MouseEvent<HTMLDivElement>): void {
    if (!props.pickingColor) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const u = clamp((e.clientX - rect.left) / rect.width, 0, 1);
    const v = clamp((e.clientY - rect.top) / rect.height, 0, 1);
    pickColorAt(u, v);
  }

  // Maps a click at (u, v) — fractions (0..1) of the DISPLAYED (already
  // cropped) banner area — to a pixel in the ORIGINAL media at natural
  // resolution, then samples a small 5x5 window centred on that pixel
  // (clamped to the source's bounds) and averages it. A single sampled
  // pixel can be an outlier under video/JPEG compression artefacts, which
  // throws off the chroma colour; averaging a small neighbourhood is more
  // representative of the true background colour while staying cheap.
  function pickColorAt(u: number, v: number): void {
    const crop = props.value.crop;
    const l = crop.left / 100;
    const r = crop.right / 100;
    const tp = crop.top / 100;
    const b = crop.bottom / 100;

    let naturalW = 0;
    let naturalH = 0;
    let source: CanvasImageSource | null = null;

    if (props.banner.kind === 'image') {
      const img = pickImgRef.current;
      if (!img || !img.naturalWidth || !img.naturalHeight) {
        // Click landed before the sampling <img> finished loading — don't
        // fail silently, tell the caller so it can surface a hint and drop
        // out of picking mode.
        props.onPickUnavailable?.('not-ready');
        return;
      }
      naturalW = img.naturalWidth;
      naturalH = img.naturalHeight;
      source = img;
    } else {
      const video = pickVideoRef.current;
      if (!video || !video.videoWidth || !video.videoHeight) {
        // Same as above but for the hidden sampling <video> — videoWidth/
        // videoHeight are 0 until its first frame decodes.
        props.onPickUnavailable?.('not-ready');
        return;
      }
      naturalW = video.videoWidth;
      naturalH = video.videoHeight;
      source = video;
    }

    const sx = Math.round(clamp((l + u * (1 - l - r)) * naturalW, 0, naturalW - 1));
    const sy = Math.round(clamp((tp + v * (1 - tp - b)) * naturalH, 0, naturalH - 1));

    // 5x5 window centred on (sx, sy), clamped to the source's bounds — the
    // clamped extent can be smaller than 5 near an edge/corner.
    const sx0 = clamp(sx - 2, 0, naturalW - 1);
    const sy0 = clamp(sy - 2, 0, naturalH - 1);
    const sx1 = clamp(sx + 2, 0, naturalW - 1);
    const sy1 = clamp(sy + 2, 0, naturalH - 1);
    const w = Math.max(1, sx1 - sx0 + 1);
    const h = Math.max(1, sy1 - sy0 + 1);

    if (!pickCanvasRef.current) pickCanvasRef.current = document.createElement('canvas');
    const canvas = pickCanvasRef.current;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    try {
      ctx.clearRect(0, 0, w, h);
      ctx.drawImage(source, sx0, sy0, w, h, 0, 0, w, h);
      const px = ctx.getImageData(0, 0, w, h).data;
      let sumR = 0;
      let sumG = 0;
      let sumB = 0;
      let count = 0;
      for (let i = 0; i < px.length; i += 4) {
        const a = px[i + 3] ?? 0;
        if (a === 0) continue; // ignore fully-transparent samples in the window
        sumR += px[i] ?? 0;
        sumG += px[i + 1] ?? 0;
        sumB += px[i + 2] ?? 0;
        count++;
      }
      if (count === 0) {
        // Every pixel in the window was fully transparent — nothing to
        // average, treat the pick as unsuccessful rather than emit black.
        props.onPickUnavailable?.();
        return;
      }
      const hex = [sumR / count, sumG / count, sumB / count]
        .map((c) => Math.round(c).toString(16).padStart(2, '0'))
        .join('')
        .toUpperCase();
      props.onPickColor?.(hex);
    } catch {
      // SecurityError — tainted canvas (R2 CORS missing on the banner
      // media). Don't call onPickColor with garbage; tell the caller
      // sampling is unavailable so it can surface that in the UI.
      props.onPickUnavailable?.();
    }
  }

  // Esc exits crop mode
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape' && cropMode) setCropMode(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cropMode]);

  // ---- Render ----
  return (
    <div
      ref={canvasRef}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: 'relative',
        aspectRatio: '9 / 16',
        maxHeight: '75vh',
        margin: '0 auto',
        background: '#000',
        borderRadius: 6,
        overflow: 'hidden',
        userSelect: 'none',
      }}
    >
      {/* Clip background — first frame */}
      <video
        src={props.clipPresignedUrl}
        muted
        playsInline
        preload="metadata"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
      />

      {/* Guide lines: video-area inner edges (yellow) + clip center (cyan).
          Banner edges AND banner center snap to either kind — see snapY/snapX. */}
      <div style={guideStyle(BANNER_SNAP_VIDEO_TOP_PCT)} />
      <div style={guideStyle(BANNER_SNAP_VIDEO_BOTTOM_PCT)} />
      <div style={guideStyle(50, 'rgba(0, 200, 220, 0.55)')} />
      <div style={vGuideStyle(50, 'rgba(0, 200, 220, 0.55)')} />

      {/* Crop-mode toggle (top-left).
          type="button" — without it, <button> defaults to type="submit" and
          clicking would submit the parent <form> in BannerModal, closing the
          modal. Same applies to the snap buttons below. */}
      <button
        type="button"
        onClick={() => setCropMode((m) => !m)}
        disabled={props.pickingColor}
        style={{
          position: 'absolute', left: 8, top: 8, zIndex: 3,
          padding: '4px 8px', fontSize: 11,
          background: cropMode ? 'var(--neon)' : 'rgba(0,0,0,0.55)',
          color: cropMode ? '#000' : '#fff', border: '1px solid var(--line)', borderRadius: 4,
          cursor: props.pickingColor ? 'not-allowed' : 'pointer',
          opacity: props.pickingColor ? 0.5 : 1,
        }}
        title="Crop / Place"
      >
        ✂ {cropMode ? 'Crop (ESC)' : 'Crop'}
      </button>

      {/* Quick-snap toolbar (top-right) — lives in the editor because it
          needs croppedAspect/bannerHeightPct, which the editor owns. Modal
          shouldn't have to know banner aspect math. */}
      <div style={{ position: 'absolute', right: 8, top: 8, zIndex: 3, display: 'flex', gap: 4 }}>
        <button type="button" onClick={() => snapTo('top-band')} style={snapButtonStyle} title="Snap to top band">↑ Top</button>
        <button type="button" onClick={() => snapTo('center')}   style={snapButtonStyle} title="Snap to center">— Center</button>
        <button type="button" onClick={() => snapTo('bottom-band')} style={snapButtonStyle} title="Snap to bottom band">↓ Bottom</button>
      </div>

      {/* Banner overlay */}
      <div
        onPointerDown={onBannerPointerDown}
        onClick={onBannerClick}
        style={{
          position: 'absolute',
          left: `${props.value.positionXPct}%`,
          top: `${props.value.positionYPct}%`,
          width: `${bannerWidthPct}%`,
          height: `${bannerHeightPct}%`,
          cursor: props.pickingColor ? 'crosshair' : cropMode ? 'default' : 'move',
          outline: '1px dashed rgba(255,255,255,0.6)',
        }}
      >
        <BannerMedia
          media={props.banner}
          crop={props.value.crop}
          onAspect={setMediaAspect}
          cropMode={cropMode}
          chromaPreview={cropMode ? null : (props.chromaPreview ?? null)}
        />

        {/* Handles — hidden while picking a colour (drag/resize/crop disabled). */}
        {!props.pickingColor && (['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'] as const).map((h) => {
          // In crop mode only edge handles (n/s/e/w) are useful.
          if (cropMode && !'nsew'.includes(h)) return null;
          return (
            <div
              key={h}
              onPointerDown={(e) => onHandlePointerDown(h, e)}
              style={{ ...handleStyle(h), background: cropMode ? 'var(--neon)' : '#fff' }}
            />
          );
        })}
      </div>

      {/* Hidden eyedropper sampling source — the ORIGINAL banner media at
          natural size, always kept in sync with props.banner.mediaUrl,
          independent of the on-screen render path (raw <img>/<video> or the
          keyed canvas preview). display:none still decodes/plays video
          frames in-DOM (same assumption ChromaKeyedMedia's hidden <video>
          already relies on for its RAF loop). */}
      {props.banner.kind === 'image' ? (
        <img
          ref={pickImgRef}
          crossOrigin="anonymous"
          src={props.banner.mediaUrl}
          alt=""
          style={{ display: 'none' }}
        />
      ) : (
        // This is a SEPARATE <video> element with its own independent
        // playback position from whatever's on screen (raw banner or the
        // ChromaKeyedMedia preview) — for a video banner with a NON-uniform,
        // animated background, the frame sampled here can differ from the
        // frame the moderator is currently looking at. Not a concern for a
        // chroma background specifically, since by definition it's uniform
        // across the whole clip.
        <video
          ref={pickVideoRef}
          crossOrigin="anonymous"
          src={props.banner.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
}

// ---- helpers ----
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
function signForHandle(h: string): { x: number; y: number } {
  return {
    x: h.includes('e') ? 1 : h.includes('w') ? -1 : 0,
    y: h.includes('s') ? 1 : h.includes('n') ? -1 : 0,
  };
}
function anchorForHandle(
  h: string,
  origX: number,
  origY: number,
  origSize: number,
  bannerH: number,
): { anchorRight: boolean; anchorBottom: boolean; x: number; y: number } {
  // X-axis: handles with 'w' (nw/w/sw) pin the RIGHT edge — banner grows leftward.
  // Y-axis: handles with 'n' (nw/n/ne) pin the BOTTOM edge — banner grows upward.
  // The two axes are independent, so we return two booleans (not one tag).
  // Prior single-`kind` shape silently dropped the Y-anchor branch for nw/n/w/sw
  // because no code path ever returned kind:'bottom'; banners teleported vertically.
  return {
    anchorRight: h.includes('w'),
    anchorBottom: h.includes('n'),
    x: h.includes('w') ? origX + origSize : origX,
    y: h.includes('n') ? origY + bannerH : origY,
  };
}
const snapButtonStyle: CSSProperties = {
  padding: '4px 8px',
  fontSize: 11,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  border: '1px solid var(--line)',
  borderRadius: 4,
  cursor: 'pointer',
};

function guideStyle(yPct: number, color = 'rgba(255, 220, 0, 0.6)'): CSSProperties {
  return {
    position: 'absolute',
    left: 0,
    right: 0,
    top: `${yPct}%`,
    borderTop: `1px dashed ${color}`,
    pointerEvents: 'none',
    zIndex: 2,
  };
}
function vGuideStyle(xPct: number, color: string): CSSProperties {
  return {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: `${xPct}%`,
    borderLeft: `1px dashed ${color}`,
    pointerEvents: 'none',
    zIndex: 2,
  };
}
function handleStyle(h: string): CSSProperties {
  const pos = {
    nw: { left: -4, top: -4 }, n: { left: '50%', top: -4, marginLeft: -4 }, ne: { right: -4, top: -4 },
    e: { right: -4, top: '50%', marginTop: -4 }, se: { right: -4, bottom: -4 },
    s: { left: '50%', bottom: -4, marginLeft: -4 }, sw: { left: -4, bottom: -4 },
    w: { left: -4, top: '50%', marginTop: -4 },
  } as Record<string, CSSProperties>;
  const cursor: Record<string, string> = {
    nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize',
    se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
  };
  return {
    position: 'absolute',
    width: 10,
    height: 10,
    background: '#fff',
    border: '1px solid #000',
    borderRadius: 1,
    cursor: cursor[h],
    zIndex: 4,
    ...(pos[h] ?? {}),
  };
}

// ---- Banner media subcomponent (image OR video) with crop-clip, aspect
//      callback, and OPTIONAL chroma-key preview via <canvas>.
function BannerMedia({
  media,
  crop,
  onAspect,
  cropMode,
  chromaPreview,
}: {
  media: { mediaUrl: string; kind: 'image' | 'video' };
  crop: { top: number; right: number; bottom: number; left: number };
  onAspect: (aspect: number) => void;
  cropMode: boolean;
  chromaPreview: ChromaPreviewParams | null;
}): ReactElement {
  // Show full banner when in crop mode (with a dark mask outside the kept area).
  // Show only kept area otherwise (clip-path + scale + translate to fill the div).
  const visible = !cropMode
    ? {
        clipPath: `inset(${crop.top}% ${crop.right}% ${crop.bottom}% ${crop.left}%)`,
        transformOrigin: '0 0',
        transform: `scale(${100 / (100 - crop.left - crop.right)}, ${100 / (100 - crop.top - crop.bottom)}) translate(-${crop.left}%, -${crop.top}%)`,
      }
    : {};
  const commonStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'fill',
    pointerEvents: 'none',
    ...visible,
  };

  // Keyed preview path — render through a canvas instead of <img>/<video>.
  // Used only when chromaPreview is non-null (modal toggle).
  if (chromaPreview && !cropMode) {
    return (
      <ChromaKeyedMedia
        media={media}
        onAspect={onAspect}
        commonStyle={commonStyle}
        chroma={chromaPreview}
      />
    );
  }

  return (
    <>
      {media.kind === 'image' ? (
        <img
          crossOrigin="anonymous"
          src={media.mediaUrl}
          alt=""
          onLoad={(e) => {
            const el = e.currentTarget;
            if (el.naturalWidth && el.naturalHeight) onAspect(el.naturalWidth / el.naturalHeight);
          }}
          style={commonStyle}
        />
      ) : (
        <video
          crossOrigin="anonymous"
          src={media.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          onLoadedMetadata={(e) => {
            const el = e.currentTarget;
            if (el.videoWidth && el.videoHeight) onAspect(el.videoWidth / el.videoHeight);
          }}
          style={commonStyle}
        />
      )}
      {cropMode && (
        // Dark mask over the trimmed area (keeps the kept rectangle bright).
        // Box-shadow trick is the same one BannerCropEditor used.
        <div
          style={{
            position: 'absolute',
            left: `${crop.left}%`,
            top: `${crop.top}%`,
            right: `${crop.right}%`,
            bottom: `${crop.bottom}%`,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
            pointerEvents: 'none',
          }}
        />
      )}
    </>
  );
}

/** Per-pixel chroma-key approximation in a <canvas>. RGB-distance based —
 *  cheaper than ffmpeg's YUV-distance but close enough for a visual preview.
 *  Images: process once on load + on chroma-param change.
 *  Videos: requestAnimationFrame loop drawing the current frame.
 *  Falls back to raw rendering if the canvas is tainted (R2 CORS missing). */
function ChromaKeyedMedia({
  media,
  onAspect,
  commonStyle,
  chroma,
}: {
  media: { mediaUrl: string; kind: 'image' | 'video' };
  onAspect: (aspect: number) => void;
  commonStyle: CSSProperties;
  chroma: ChromaPreviewParams;
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tainted, setTainted] = useState(false);

  // Parse '#RRGGBB' or '0xRRGGBB' → r/g/b. NaN-safe: bail out, render raw.
  const cleaned = chroma.color.replace(/^0x/i, '').replace(/^#/, '').padEnd(6, '0').slice(0, 6);
  const chromaR = parseInt(cleaned.slice(0, 2), 16);
  const chromaG = parseInt(cleaned.slice(2, 4), 16);
  const chromaB = parseInt(cleaned.slice(4, 6), 16);
  const colorValid = !Number.isNaN(chromaR) && !Number.isNaN(chromaG) && !Number.isNaN(chromaB);

  // ffmpeg's chromakey filter compares pixels in YCbCr-space (Cb/Cr-distance
  // only — luminance ignored), which is why it cheerfully kills shaded greens
  // that pure RGB-distance preserves. Match that behaviour: convert both
  // chroma color and each pixel to BT.601 Cb/Cr, take Euclidean distance.
  // Max distance ≈ 255 (the practical Cb/Cr range is ~ [-128, +128] each).
  // similarity 0..1 maps proportionally to that max.
  const chromaCb = -0.169 * chromaR - 0.331 * chromaG + 0.500 * chromaB + 128;
  const chromaCr =  0.500 * chromaR - 0.419 * chromaG - 0.081 * chromaB + 128;
  const innerThresh = chroma.similarity * 255;
  const outerThresh = innerThresh + chroma.blend * 255;

  useEffect(() => {
    if (!colorValid || tainted) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    function processFrame(source: CanvasImageSource, w: number, h: number): boolean {
      if (!ctx || !canvas) return false;
      canvas.width = w;
      canvas.height = h;
      try {
        ctx.drawImage(source, 0, 0, w, h);
        const data = ctx.getImageData(0, 0, w, h);
        const px = data.data;
        // px is a Uint8ClampedArray; indices in bounds are always defined.
        // The ?? 0 is solely to placate TS's noUncheckedIndexedAccess.
        for (let i = 0; i < px.length; i += 4) {
          const r = px[i] ?? 0;
          const g = px[i + 1] ?? 0;
          const b = px[i + 2] ?? 0;
          const pixCb = -0.169 * r - 0.331 * g + 0.500 * b + 128;
          const pixCr =  0.500 * r - 0.419 * g - 0.081 * b + 128;
          const dCb = pixCb - chromaCb;
          const dCr = pixCr - chromaCr;
          const dist = Math.sqrt(dCb * dCb + dCr * dCr);
          if (dist < innerThresh) px[i + 3] = 0;
          else if (dist < outerThresh)
            px[i + 3] = Math.round((255 * (dist - innerThresh)) / Math.max(outerThresh - innerThresh, 1));
        }
        ctx.putImageData(data, 0, 0);
        return true;
      } catch {
        // SecurityError from getImageData on a tainted canvas (CORS missing
        // on the banner image). Fall back to raw rendering.
        setTainted(true);
        return false;
      }
    }

    if (media.kind === 'image') {
      // crossOrigin='anonymous' is REQUIRED for keying — without it the
      // browser doesn't send an Origin header, R2 doesn't return CORS
      // response headers, and drawImage taints the canvas regardless of
      // R2's bucket-level CORS policy. If R2 isn't configured to accept
      // CORS, the image load fails → onerror → setTainted → raw <img>
      // fallback (visible green-screen, no preview but no transparency).
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) onAspect(img.naturalWidth / img.naturalHeight);
        processFrame(img, img.naturalWidth, img.naturalHeight);
      };
      img.onerror = () => setTainted(true);
      img.src = media.mediaUrl;
      return;
    }

    // Video — RAF loop processing the current frame. Throttled by RAF
    // itself (~60fps); the per-pixel loop is the heavy cost.
    const video = videoRef.current;
    if (!video) return;
    let rafId = 0;
    let stopped = false;
    function loop(): void {
      if (stopped || !video) return;
      if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
        if (!processFrame(video, video.videoWidth, video.videoHeight)) {
          stopped = true;
          return;
        }
      }
      rafId = requestAnimationFrame(loop);
    }
    rafId = requestAnimationFrame(loop);
    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
    };
  }, [media.mediaUrl, media.kind, chromaCb, chromaCr, innerThresh, outerThresh, colorValid, tainted, onAspect]);

  // Fallback path — render raw <img>/<video> exactly as the non-preview path.
  // Overlay a small badge explaining why the preview is unavailable so the
  // user knows the toggle isn't broken — they need R2 CORS for the page's
  // origin to be able to read the media into canvas.
  if (!colorValid || tainted) {
    return (
      <>
        {media.kind === 'image' ? (
          <img
            crossOrigin="anonymous"
            src={media.mediaUrl}
            alt=""
            onLoad={(e) => {
              const el = e.currentTarget;
              if (el.naturalWidth && el.naturalHeight) onAspect(el.naturalWidth / el.naturalHeight);
            }}
            style={commonStyle}
          />
        ) : (
          <video
            crossOrigin="anonymous"
            src={media.mediaUrl}
            autoPlay
            loop
            muted
            playsInline
            onLoadedMetadata={(e) => {
              const el = e.currentTarget;
              if (el.videoWidth && el.videoHeight) onAspect(el.videoWidth / el.videoHeight);
            }}
            style={commonStyle}
          />
        )}
        {tainted && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              padding: '4px 8px',
              background: 'rgba(220, 38, 38, 0.85)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 600,
              textAlign: 'center',
              pointerEvents: 'none',
            }}
            title="Add the dashboard origin to the R2 bucket's CORS AllowedOrigins for GET/HEAD."
          >
            preview unavailable — R2 CORS missing
          </div>
        )}
      </>
    );
  }

  return (
    <>
      {media.kind === 'video' && (
        // Hidden source — never visible. The canvas owns the visual output.
        // crossOrigin='anonymous' is REQUIRED for keying (browser must send
        // Origin header so R2 returns CORS response headers; otherwise the
        // canvas is tainted even for a 'successfully' loaded video and
        // getImageData throws). If R2 lacks CORS the video load fails →
        // onError → setTainted → raw <video> fallback (the user will see
        // the green screen but at least the banner is visible).
        //
        // IMPORTANT: crossOrigin MUST come before src in JSX. React applies
        // props in declaration order; if src is set first, the browser begins
        // loading without an Origin header, and adding crossOrigin afterward
        // is too late — the canvas stays tainted.
        <video
          ref={videoRef}
          crossOrigin="anonymous"
          src={media.mediaUrl}
          autoPlay
          loop
          muted
          playsInline
          onLoadedMetadata={(e) => {
            const el = e.currentTarget;
            if (el.videoWidth && el.videoHeight) onAspect(el.videoWidth / el.videoHeight);
          }}
          onError={() => setTainted(true)}
          style={{ display: 'none' }}
        />
      )}
      <canvas ref={canvasRef} style={commonStyle} />
    </>
  );
}
